<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import SiteAdminService from '@/services/SiteAdminService'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import SiteRichTextEditor from '@/components/Sites/SiteRichTextEditor.vue'
import { urlError, siteAddress } from '@/Utils/siteUrl'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconChevronUp from '@/components/icon/IconChevronUp.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'

// Static pages (About, Terms, …) in the site footer / nav, or external links.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()

const loading = ref(true)
const items = ref([])
const loadError = ref(null)
const modalOpen = ref(false)
const saving = ref(false)
const error = ref(null)
const editing = ref(null)
const form = ref(blank())
const pendingDelete = ref(null)
const reordering = ref(false)

// Where the page is linked from (#31). Sent as `placement` only when the
// owner changes it: the runtime's PATCH rejects unknown keys, so an untouched
// form keeps working against a runtime that does not know the field yet.
const PLACEMENT_DEFAULT = 'footer'
const placementOptions = [
	{ value: 'nav', text: 'Navigation' },
	{ value: 'footer', text: 'Footer' },
	{ value: 'both', text: 'Navigation and footer' },
]
const placementLabel = (p) => placementOptions.find((o) => o.value === p)?.text || 'Footer'
const placementTouched = ref(false)

function blank() {
	return { title: '', url: '', content: '', external: false, published: true, color: '#FFFFFF', placement: PLACEMENT_DEFAULT }
}

const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 255)
const pathValid = computed(() => /^[a-z0-9-]{1,255}$/.test(form.value.url.trim()))
// External pages need a full http(s) URL; hosted pages a slug.
const urlFieldError = computed(() => {
	const v = form.value.url.trim()
	if (form.value.external) return v ? urlError(v) : null
	return v && !pathValid.value ? 'Lowercase letters, digits and dashes only' : null
})
const urlValid = computed(() => (form.value.external ? Boolean(form.value.url.trim()) && !urlFieldError.value : pathValid.value))
const canSubmit = computed(() => form.value.title.trim() && urlValid.value && !saving.value)

const onTitleInput = () => {
	if (!editing.value && !form.value.external && !touchedUrl.value) form.value.url = slugify(form.value.title)
}
const touchedUrl = ref(false)
const setExternal = (external) => {
	if (form.value.external === external) return
	form.value.external = external
	// A slug is useless as a URL and vice versa: start the field over.
	if (!touchedUrl.value || !form.value.url.trim()) form.value.url = external ? '' : slugify(form.value.title)
}
const setPlacement = (p) => { form.value.placement = p; placementTouched.value = true }

const load = async () => {
	try {
		const res = await SiteAdminService.listPages(props.siteId)
		items.value = res?.items || []
		loadError.value = null
	} catch (err) {
		loadError.value = err
	}
}
onMounted(async () => { loading.value = true; await load(); loading.value = false })

const openCreate = () => { editing.value = null; form.value = blank(); touchedUrl.value = false; placementTouched.value = false; error.value = null; modalOpen.value = true }
const openEdit = (page) => {
	editing.value = page
	form.value = {
		title: page.title || '',
		url: page.url || '',
		content: page.content || '',
		external: Boolean(page.external),
		published: page.published !== false,
		color: page.color || '#FFFFFF',
		placement: placementOptions.some((o) => o.value === page.placement) ? page.placement : PLACEMENT_DEFAULT,
	}
	touchedUrl.value = true
	placementTouched.value = false
	error.value = null
	modalOpen.value = true
}

const save = async () => {
	if (!canSubmit.value) return
	saving.value = true
	error.value = null
	const payload = {
		title: form.value.title.trim(),
		url: form.value.url.trim(),
		content: form.value.external ? '' : form.value.content,
		external: form.value.external,
		published: form.value.published,
		color: form.value.color || '#FFFFFF',
	}
	if (placementTouched.value) payload.placement = form.value.placement
	try {
		if (editing.value) await SiteAdminService.updatePage(props.siteId, editing.value.id, payload)
		else await SiteAdminService.createPage(props.siteId, payload)
		notify({ group: 'success', text: editing.value ? 'Page updated.' : 'Page created.' })
		modalOpen.value = false
		await load()
	} catch (err) {
		error.value = err.message || 'Could not save the page.'
	}
	saving.value = false
}

const requestDelete = (page) => { pendingDelete.value = page; showModal('delete-site-page') }
const confirmDelete = async () => {
	if (!pendingDelete.value) return
	try {
		await SiteAdminService.deletePage(props.siteId, pendingDelete.value.id)
		notify({ group: 'success', text: 'Page deleted.' })
		await load()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not delete the page.' })
	}
	pendingDelete.value = null
}

const move = async (index, delta) => {
	const target = index + delta
	if (target < 0 || target >= items.value.length || reordering.value) return
	const ids = items.value.map((i) => i.id)
	const [moved] = ids.splice(index, 1)
	ids.splice(target, 0, moved)
	reordering.value = true
	try {
		const res = await SiteAdminService.reorderPages(props.siteId, ids)
		if (res?.items) items.value = res.items
		else await load()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not reorder.' })
	}
	reordering.value = false
}
</script>

<template>
	<div>
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1 basis-72">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Pages</h2>
				<p class="mb-0 text-tiny text-surface-7">Static pages such as About or Terms, or links to external sites. Shown in the site navigation and footer.</p>
			</div>
			<FormButton size="md" class="shrink-0" @click="openCreate">
				<icon-base class="mr-1.5" :width="14" :height="14"><icon-plus /></icon-base>
				New page
			</FormButton>
		</div>

		<Alert v-if="loadError" type="error" class="mb-4">{{ loadError.message }}</Alert>
		<Spinner v-if="loading" text="Loading pages…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<EmptyState v-else-if="!items.length" title="No pages yet" subtitle="Add an About page, terms of service, or a link to your main website.">
			<template #actions>
				<FormButton class="w-64 justify-center" @click="openCreate">New page</FormButton>
			</template>
		</EmptyState>

		<ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
			<li v-for="(page, index) in items" :key="page.id" class="rounded-lg bg-surface-2">
				<div class="flex items-center gap-3 px-3.5 py-3">
					<div class="flex shrink-0 flex-col">
						<button type="button" class="text-surface-7 hover:text-white disabled:opacity-30" :disabled="index === 0 || reordering" title="Move up" @click="move(index, -1)">
							<icon-base :width="14" :height="14"><icon-chevron-up /></icon-base>
						</button>
						<button type="button" class="text-surface-7 hover:text-white disabled:opacity-30" :disabled="index === items.length - 1 || reordering" title="Move down" @click="move(index, 1)">
							<icon-base :width="14" :height="14"><icon-chevron-down /></icon-base>
						</button>
					</div>
					<div class="min-w-0 flex-1">
						<div class="flex flex-wrap items-center gap-2">
							<h3 class="mb-0 truncate text-sm font-medium text-white">{{ page.title }}</h3>
							<StatusPill v-if="page.external" pill size="sm" variant="muted">External link</StatusPill>
							<StatusPill v-if="page.placement && page.placement !== 'footer'" pill size="sm" variant="muted">{{ placementLabel(page.placement) }}</StatusPill>
							<StatusPill v-if="page.published === false" pill size="sm" variant="muted">Hidden</StatusPill>
						</div>
						<p class="mb-0 truncate text-tiny text-surface-7" :title="page.external ? page.url : `${siteAddress(site)}/${page.url}`">{{ page.external ? page.url : `${siteAddress(site)}/${page.url}` }}</p>
					</div>
					<div class="flex shrink-0 items-center gap-1">
						<FormButton is-icon type="secondary" size="sm" title="Edit" @click="openEdit(page)">
							<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
						</FormButton>
						<FormButton is-icon type="danger" size="sm" title="Delete" @click="requestDelete(page)">
							<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
						</FormButton>
					</div>
				</div>
			</li>
		</ul>

		<ModalBox v-model="modalOpen" :title="editing ? 'Edit page' : 'New page'" size="md" scroll-body wrapper-classes="lg:h-auto lg:max-h-[90vh]">
			<form class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" @submit.prevent="save">
				<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>
				<FormInput id="page-title" v-model="form.title" label="Title" placeholder="e.g. About us" input-classes="placeholder:text-surface-6" :maxlength="255" @update:model-value="onTitleInput" />

				<div class="min-w-0">
					<p class="mb-2 text-tiny font-medium text-surface-text-1">Type</p>
					<TabGroup type="segmented" data-testid="page-type">
						<TabItem :is-active="!form.external" @click="setExternal(false)">Page on this site</TabItem>
						<TabItem :is-active="form.external" @click="setExternal(true)">Link to another site</TabItem>
					</TabGroup>
				</div>

				<div class="min-w-0">
					<FormInput id="page-url" v-model="form.url" :label="form.external ? 'Link URL' : 'Path'" :placeholder="form.external ? 'https://example.com/about' : 'about-us'"
						:is-error="Boolean(urlFieldError)" class="min-w-0" @update:model-value="touchedUrl = true" />
					<p v-if="urlFieldError" class="mb-0 mt-1.5 text-tiny text-error">{{ urlFieldError }}</p>
					<p v-else-if="form.external" class="mb-0 mt-1.5 text-tiny text-surface-7">Opens in a new tab from the navigation or footer.</p>
					<p v-else class="mb-0 mt-1.5 truncate text-tiny text-surface-7">{{ siteAddress(site) }}/{{ form.url || 'path' }}</p>
				</div>

				<FormSelect id="page-placement" :model-value="form.placement" label="Show in" :options="placementOptions" @update:model-value="setPlacement" />

				<SiteRichTextEditor v-if="!form.external" id="page-content" v-model="form.content" label="Content" placeholder="Tell viewers about your channel, your terms, how to reach you…" />

				<div class="flex flex-wrap items-center gap-4">
					<FormSwitch id="page-published" v-model="form.published" :label="form.published ? 'Visible' : 'Hidden'" />
					<label class="flex items-center gap-2 text-tiny text-surface-8">
						Text colour
						<input v-model="form.color" type="color" class="size-7 cursor-pointer rounded border-0 bg-transparent">
					</label>
				</div>
			</form>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!canSubmit" :is-loading="saving" @click="save">{{ editing ? 'Save' : 'Create page' }}</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="modalOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>

		<ConfirmModal modal-id="delete-site-page" modal-type="danger" ok-text="Delete" cancel-text="Cancel"
			title="Delete this page?" :message="`&quot;${pendingDelete?.title || ''}&quot; is removed from the site.`" @modal-confirm="confirmDelete" />
	</div>
</template>
